import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { PromptPowerUp } from '../../components/PromptPowerUp';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { ProcessingAnimation } from '../../components/ProcessingAnimation';
import { Film, Sparkles, Download, Save, RefreshCw, Clock, Zap, Crown, Type, Layers, ImageIcon, Upload, X, Image } from 'lucide-react';
import { API, GenerationProgress, downloadFile } from './shared';
import { useIdempotencyKey } from '../../lib/useIdempotencyKey';
import { useStudioErrorHandler } from '../../components/SubmissionUncertainModal';
import { useHubPricing, formatCreditRange } from '../../lib/useHubPricing';
import { useStudioHealth, StudioHealthBanner } from '../../lib/useStudioHealth';

const ASPECT_RATIOS = ['auto', '16:9', '9:16'];

function ImageUploadZone({ label, value, onChange, testId }) {
  const fileRef = useRef(null);
  const inputId = `video-upload-${testId}`;
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Max file size is 10MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/api/creative-studio/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleRemove = () => { onChange(''); };

  if (value) {
    return (
      <div className="relative group" data-testid={testId}>
        <div className="relative rounded-lg overflow-hidden border border-gray-border bg-gray-50">
          <img src={value} alt={label} className="w-full h-24 object-cover" />
          <button
            onClick={handleRemove}
            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`${testId}-remove`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
        <p className="text-[10px] text-gray-400 mt-1 truncate">{label}</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all",
        dragOver ? "border-blue-400 bg-blue-50" : "border-gray-200 hover:border-gray-300 bg-gray-50/50"
      )}
      onClick={() => fileRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      data-testid={testId}
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
      />
      {uploading ? (
        <RefreshCw className="h-5 w-5 mx-auto text-blue-400 animate-spin" />
      ) : (
        <Upload className="h-5 w-5 mx-auto text-gray-300" />
      )}
      <p className="text-[10px] text-gray-400 mt-1">{uploading ? 'Uploading...' : label}</p>
    </div>
  );
}

export function VideoStudio({ onSave }) {
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState('text_to_video');
  const [aspect, setAspect] = useState('16:9');
  const [tier, setTier] = useState('standard');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMsg, setProgressMsg] = useState('');
  const [result, setResult] = useState(null);
  const [startImage, setStartImage] = useState('');
  const [endImage, setEndImage] = useState('');
  const [refImages, setRefImages] = useState(['', '', '']);
  const pollRef = useRef(null);

  // v3.0 Kaharian: stable Idempotency-Key + uncertain-submission handler
  const idempotencyKey = useIdempotencyKey('konnect-video', [
    prompt, mode, aspect, tier, startImage, endImage, refImages.join(','),
  ]);
  const { handleStudioError, SubmissionUncertainDialog } = useStudioErrorHandler();
  const { getPrice, isLoading: pricingLoading } = useHubPricing();
  const videoPriceRow = getPrice('VIDEO.generate_8s');

  // v3.2: proactive provider-health probe + auto-retry via shared hook.
  const { health, cooldown, onProvidersUnavailable, pendingRetryRef } = useStudioHealth(
    'VIDEO',
    { onRecover: () => generateInternal() }
  );

  const generateInternal = async () => {
    if (!prompt.trim()) { toast.error('Enter a prompt'); return; }
    setGenerating(true); setResult(null); setProgress(0);
    setProgressMsg('Initializing video generation...');
    const progressInterval = setInterval(() => {
      setProgress(p => { if (p >= 85) return 85; return p + Math.random() * 3; });
      setProgressMsg(['Composing scenes...', 'Rendering frames...', 'Applying motion...', 'Building transitions...', 'Processing video...'][Math.floor(Math.random() * 5)]);
    }, 2500);

    try {
      const res = await axios.post(`${API}/api/creative-studio/generate/video`, {
        prompt: prompt.trim(), mode, aspect_ratio: aspect, tier,
        start_image: mode === 'image_to_video' ? startImage : '',
        end_image: mode === 'image_to_video' ? endImage : '',
        reference_images: mode === 'reference' ? refImages.filter(Boolean) : [],
        idempotency_key: idempotencyKey,
      });
      if (res.data.status === 'completed' && res.data.url) {
        clearInterval(progressInterval); setProgress(100); setProgressMsg('Video ready!');
        setTimeout(() => { setResult(res.data); setGenerating(false); }, 500);
      } else if (res.data.task_id) {
        setProgressMsg('Processing typically takes 3-5 minutes. Polling automatically...');
        pollRef.current = setInterval(async () => {
          try {
            const poll = await axios.get(`${API}/api/creative-studio/task-status/${res.data.task_id}`);
            if (poll.data.progress > 0) setProgress(Math.min(poll.data.progress, 95));
            // ADDENDUM v3.2.2-r1: canonical read — data.url + data.status === "ready"/"completed".
            const videoUrl = poll.data.url || '';
            const isComplete = poll.data.status === 'completed' || poll.data.status === 'ready';
            if (isComplete && videoUrl) {
              clearInterval(pollRef.current); clearInterval(progressInterval);
              setProgress(100); setProgressMsg('Video ready!');
              setTimeout(() => { setResult({ ...res.data, url: videoUrl, status: 'completed' }); setGenerating(false); }, 500);
            } else if (poll.data.status === 'failed' || poll.data.status === 'error') {
              clearInterval(pollRef.current); clearInterval(progressInterval);
              toast.error('Video generation failed');
              setResult({ ...res.data, status: 'failed' }); setGenerating(false);
            }
          } catch { /* continue polling */ }
        }, 5000);
        setTimeout(() => {
          if (pollRef.current) {
            clearInterval(pollRef.current); clearInterval(progressInterval);
            toast.info('Video is still being processed. Check back in Library shortly.');
            setResult(null); setGenerating(false);
          }
        }, 360000);
      } else {
        clearInterval(progressInterval); setResult(res.data); setGenerating(false);
      }
    } catch (err) {
      clearInterval(progressInterval);
      // v3.2: on PROVIDERS_UNAVAILABLE, arm auto-retry and start the countdown.
      const detail = err?.response?.data?.detail;
      const isProvidersDown = err?.response?.status === 503 && typeof detail?.code === 'string' && detail.code.endsWith('_PROVIDERS_UNAVAILABLE');
      if (isProvidersDown) {
        onProvidersUnavailable(detail, { armRetry: true });
        toast.error(`${detail.message || 'Temporarily unavailable'} · Auto-retrying in 30s…`, { duration: 5000, id: 'video-providers-down' });
      } else {
        handleStudioError(err, { category: 'VIDEO', idempotencyKey, fallbackMessage: 'Video generation failed' });
      }
      setGenerating(false);
    }
  };

  const generate = generateInternal;

  useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  const handleSave = () => {
    if (!result?.url) return;
    onSave({ type: 'video', prompt: result.prompt, url: result.url, tier: result.tier, credits_used: result.credits_used, title: result.prompt });
  };

  const updateRefImage = (index, url) => {
    setRefImages(prev => { const u = [...prev]; u[index] = url; return u; });
  };

  const MODES = [
    { key: 'text_to_video', label: 'Text to Video', desc: 'Prompt only', icon: Type },
    { key: 'image_to_video', label: 'Image to Video', desc: 'Start / End frames', icon: ImageIcon },
    { key: 'reference', label: 'Reference', desc: 'Up to 3 images', icon: Layers },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="video-studio">
      <SubmissionUncertainDialog />
      <div className="lg:col-span-5">
        <StudioHealthBanner category="VIDEO" health={health} cooldown={cooldown} pendingRetry={pendingRetryRef.current} />
      </div>
      <div className="lg:col-span-2 space-y-4">
        <Card className="bg-white border-gray-border">
          <CardContent className="pt-4 space-y-4">
            <div>
              <Label className="text-xs font-medium">Generation Type</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {MODES.map(m => (
                  <button key={m.key} onClick={() => setMode(m.key)} className={cn("p-2.5 rounded-xl border-2 text-center transition-all",
                    mode === m.key ? "border-blue-400 bg-blue-50" : "border-gray-border hover:border-gray-300")} data-testid={`mode-${m.key}`}>
                    <m.icon className={cn("h-4 w-4 mx-auto mb-1", mode === m.key ? "text-blue-600" : "text-gray-400")} />
                    <p className="text-[10px] font-medium leading-tight">{m.label}</p>
                    <p className="text-[8px] text-gray-400">{m.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setTier('standard')} className={cn("p-2.5 rounded-xl border-2 text-left transition-all",
                tier === 'standard' ? "border-amber-400 bg-amber-50" : "border-gray-border hover:border-gray-300")} data-testid="video-tier-standard">
                <Zap className="h-3.5 w-3.5 text-amber-500 mb-0.5" /><p className="text-xs font-bold">Standard</p>
                <p className="text-[10px] text-gray-medium">{pricingLoading ? '…' : formatCreditRange(videoPriceRow)}</p>
              </button>
              <button onClick={() => setTier('pro')} className={cn("p-2.5 rounded-xl border-2 text-left transition-all",
                tier === 'pro' ? "border-purple-400 bg-purple-50" : "border-gray-border hover:border-gray-300")} data-testid="video-tier-pro">
                <Crown className="h-3.5 w-3.5 text-purple-500 mb-0.5" /><p className="text-xs font-bold">PRO</p>
                <p className="text-[10px] text-gray-medium">Higher quality</p>
              </button>
            </div>

            {mode === 'image_to_video' && (
              <div className="space-y-3">
                <Label className="text-xs font-medium">Upload Frames</Label>
                <div className="grid grid-cols-2 gap-3">
                  <ImageUploadZone
                    label="Start Frame"
                    value={startImage}
                    onChange={setStartImage}
                    testId="start-image-upload"
                  />
                  <ImageUploadZone
                    label="End Frame (optional)"
                    value={endImage}
                    onChange={setEndImage}
                    testId="end-image-upload"
                  />
                </div>
              </div>
            )}

            {mode === 'reference' && (
              <div>
                <Label className="text-xs font-medium">Reference Images (up to 3)</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[0, 1, 2].map(i => (
                    <ImageUploadZone
                      key={i}
                      label={`Image ${i + 1}`}
                      value={refImages[i]}
                      onChange={(url) => updateRefImage(i, url)}
                      testId={`ref-image-upload-${i}`}
                    />
                  ))}
                </div>
              </div>
            )}

            <div><Label className="text-xs font-medium">Prompt</Label>
              <PromptPowerUp contextType="general">
              <Textarea placeholder="Describe the video you want to generate..." value={prompt} onChange={e => setPrompt(e.target.value)} className="mt-1 min-h-[80px] text-sm" data-testid="video-prompt" />
              </PromptPowerUp></div>

            <div>
              <Label className="text-xs font-medium">Aspect Ratio</Label>
              <div className="flex gap-1.5 mt-1">
                {ASPECT_RATIOS.map(ar => (
                  <button key={ar} onClick={() => setAspect(ar)} className={cn("px-3 py-1.5 text-xs rounded-lg border transition-all font-medium capitalize",
                    aspect === ar ? "bg-text-primary text-white border-text-primary" : "border-gray-border hover:border-gray-300")} data-testid={`vaspect-${ar}`}>{ar}</button>
                ))}
              </div>
            </div>

            <Button className="w-full h-11" onClick={generate} disabled={generating || !prompt.trim() || pricingLoading || !videoPriceRow || !health.available} data-testid="generate-video-btn">
              <Sparkles className="h-4 w-4 mr-2" />{!health.available ? `Unavailable${cooldown > 0 ? ` · ${cooldown}s` : ''}` : (!pricingLoading && !videoPriceRow ? 'Unavailable' : 'Generate Video')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-3 relative">
        <GenerationProgress active={generating} progress={Math.round(progress)} message={progressMsg} />
        {result?.url ? (
          <Card className="bg-white border-gray-border overflow-hidden" data-testid="video-result">
            <video src={result.url} controls className="w-full max-h-[400px]" />
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-blue-100 text-blue-700 text-[10px]">AI Generated</Badge>
                {result.credits_used > 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px]">{result.credits_used} credits used</Badge>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1" onClick={handleSave}><Save className="h-3.5 w-3.5 mr-1" />Save to Library</Button>
                <Button size="sm" variant="outline" onClick={() => downloadFile(result.url, `konnect_video_${Date.now()}.mp4`)}><Download className="h-3.5 w-3.5 mr-1" />Download</Button>
                <Button size="sm" variant="ghost" onClick={() => setResult(null)}><RefreshCw className="h-3.5 w-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ) : result?.task_id && !generating ? (
          <Card className="bg-white border-gray-border"><CardContent className="p-8">
            <ProcessingAnimation
              title={result.status === 'failed' ? 'Video generation failed' : 'Generating Video'}
              messages={result.status === 'failed'
                ? ['Please try again with a different prompt.']
                : ['Composing scenes...', 'Rendering frames...', 'Applying motion effects...', 'Building transitions...', 'Processing typically takes 3-5 minutes...', 'Your video is almost ready...']}
            />
            {result.credits_used > 0 && <Badge className="mt-2 bg-amber-100 text-amber-700 text-xs">{result.credits_used} credits charged</Badge>}
            <Button size="sm" variant="outline" className="mt-3" onClick={() => {
              if (result.task_id) {
                axios.get(`${API}/api/creative-studio/task-status/${result.task_id}`)
                  .then(r => {
                    if (r.data.url || r.data.videoUrl) { setResult({ ...result, url: r.data.url || r.data.videoUrl, status: 'completed' }); toast.success('Video ready!'); }
                    else toast.info(`Status: ${r.data.status} (${r.data.progress}%)`);
                  }).catch(() => toast.error('Could not check status'));
              }
            }} data-testid="check-video-status"><RefreshCw className="h-3.5 w-3.5 mr-1" />Check Status</Button>
          </CardContent></Card>
        ) : !generating ? (
          <Card className="bg-white border-gray-border"><CardContent className="p-12 text-center">
            <Film className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <p className="text-sm text-gray-medium">Your generated video will appear here</p>
          </CardContent></Card>
        ) : null}
      </div>
    </div>
  );
}
